import { useState } from "react";
import { Check as CheckIcon, Close as CloseIcon } from "@mui/icons-material";
import { Box, Button, MenuItem, Select } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DEFAULT_DRAG_HANDLE_SELECTOR } from "../../../app/constants";
import i18n from "../../../i18n";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { TextFieldLight } from "../../../common/components/styled/styled";
import {
  priceListUnits,
  SingleOverrideItem,
  TesPricingOverrideOptions,
} from "../tesPricingOverrideAPI";
import { normalizeInputValue } from "../../../app/common";
import { useAppSelector } from "../../../app/hooks";

// Helper function to check if a value is a valid numeric string
const isValidNumericString = (value: string) => {
  return /^\d*\.?\d+$/.test(value.replace(",", "."));
};

interface Props {
  tesPricingOverrideOptions: TesPricingOverrideOptions;
  originalItem: SingleOverrideItem | null;
  isLoading: boolean;
  onClose: () => void;
  handleOnSave: (item: SingleOverrideItem) => void;
  modalTitle: string;
  modalItemSelectLabel: string;
  modalItemValueLabel: string;
}

const EditTesPricingOverrideModal = ({
  tesPricingOverrideOptions,
  originalItem,
  isLoading,
  onClose,
  handleOnSave,
  modalTitle,
  modalItemValueLabel,
  modalItemSelectLabel,
}: Props) => {
  const { t } = useTranslation();

  const [selectedItem, setSelectedItem] = useState<SingleOverrideItem | null>(
    originalItem,
  );
  const [isInputValid, setIsInputValid] = useState(true);

  const translatedSupplementNames = useAppSelector(
    (state) => state.localized.supplement,
  );

  const handleOnSaveClick = (item: SingleOverrideItem) => {
    if (item) {
      handleOnSave({
        ...item,
        // Normalize the value to use dot as a decimal separator
        value: normalizeInputValue(item.value),
      });
    }
  };

  // On change handler for the option select
  const handleOnChange = (key: string) => {
    if (key) {
      setSelectedItem({
        key: key,
        value: tesPricingOverrideOptions[key]?.unitValue || "",
        unit: tesPricingOverrideOptions[key]?.unit || "",
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const normalizedValue = value.replace(",", ".");

    // Check if the value is numeric and non-negative
    const isValid =
      isValidNumericString(value) && parseFloat(normalizedValue) >= 0;

    setIsInputValid(isValid);

    setSelectedItem((prev) => {
      if (prev) {
        return {
          ...prev,
          value: value,
        };
      }
      return {
        key: "",
        value: value,
        unit: "",
      };
    });
  };

  // Create an array of override options translated names and keys
  const optionArray = Object.keys(tesPricingOverrideOptions).map((key) => ({
    key,
    name:
      translatedSupplementNames[key] !== undefined
        ? translatedSupplementNames[key]
        : key,
  }));

  // Sort the array by translated name with the locale
  optionArray.sort((a, b) => {
    const nameA = a.name || ""; // Use empty string if a.name is undefined
    const nameB = b.name || ""; // Use empty string if b.name is undefined
    return nameA.localeCompare(nameB, i18n.language);
  });

  return (
    <Box
      sx={{
        width: "572px",
        display: "flex",
        flexDirection: "column",
        borderTopRightRadius: "12px",
        borderTopLeftRadius: "12px",
      }}
    >
      <OverlaySpinner isLoading={isLoading} />
      <Box
        className={DEFAULT_DRAG_HANDLE_SELECTOR}
        sx={{
          fontSize: "16px",
          fontWeight: "600",
          padding: "0px 15px",
        }}
      >
        {modalTitle}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          padding: "15px 15px",
        }}
      >
        <Box
          sx={{
            width: "40%",
            textAlign: "end",
            marginRight: "20px",
            color: "rgba(0, 0, 0, 0.52)",
          }}
        >
          {modalItemSelectLabel}
        </Box>
        <Box sx={{ width: "60%" }}>
          <Select
            size="small"
            fullWidth
            displayEmpty
            value={selectedItem?.key || ""}
            onChange={(e) => handleOnChange(e.target.value)}
            sx={{
              backgroundColor: "rgba(249, 249, 250, 0.6)",
            }}
            disabled={!!originalItem?.key}
            renderValue={(key) =>
              key
                ? translatedSupplementNames[key] || key
                : t("common:select...")
            }
          >
            {optionArray.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "15px 15px",
        }}
      >
        <Box
          sx={{
            whiteSpace: "nowrap",
            width: "40%",
            textAlign: "end",
            marginRight: "20px",
            color: "rgba(0, 0, 0, 0.52)",
          }}
        >
          {modalItemValueLabel}
        </Box>
        <Box
          sx={{
            width: "60%",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <TextFieldLight
            size="small"
            sx={{
              width: "100px",
              border: isInputValid ? "none" : "2px solid red",
              borderRadius: "4px",
            }}
            value={selectedItem?.value || ""}
            onChange={handleInputChange}
          />
          <Box
            sx={{
              marginLeft: "10px",
              color: "rgba(0, 0, 0, 0.52)",
            }}
          >
            {(selectedItem?.unit && priceListUnits[selectedItem.unit]) || ""}
          </Box>
        </Box>
      </Box>

      <Box
        className={DEFAULT_DRAG_HANDLE_SELECTOR}
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          padding: "15px 15px",
        }}
      >
        <Button
          sx={{
            borderRadius: "15px",
            height: "30px",
          }}
          startIcon={<CloseIcon />}
          onClick={onClose}
          variant="outlined"
        >
          {t("common:cancelShort")}
        </Button>

        <Button
          disabled={!selectedItem?.key || !selectedItem?.value || !isInputValid}
          sx={{
            borderRadius: "15px",
            height: "30px",
          }}
          startIcon={<CheckIcon />}
          onClick={() => selectedItem && handleOnSaveClick(selectedItem)}
          variant="contained"
        >
          {t("common:complete")}
        </Button>
      </Box>
    </Box>
  );
};

export default EditTesPricingOverrideModal;
